import { useInfiniteQuery, useMutation } from "react-query";
import { cloneDeep } from "lodash";
import { queryClient } from "~/repository/index";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import { encodeData } from "~/utils/utils";

const STALE_TIME = Infinity;

export const useNotificationsForScreen = (config) => {
  const fetchData = ({ pageParam = 1, signal }) => {
    const params = {
      page: pageParam,
    };
    return fetch({
      url: `${Angie.api_url}/notifications/for-screen?${encodeData(params)}`,
      withHeaders: true,
      signal,
    }).then((response) => {
      const { _headers = {}, _parsedBody } = response;
      const _pagination = {
        page: Number(_headers["x-angie-paginationcurrentpage"]),
        perPage: Number(_headers["x-angie-paginationitemsperpage"]),
        total: Number(_headers["x-angie-paginationtotalitems"]),
      };
      return {
        ..._parsedBody,
        pagination: _pagination,
      };
    });
  };

  return useInfiniteQuery({
    ...config,
    staleTime: STALE_TIME,
    queryKey: ["notifications", "for-screen"],
    queryFn: fetchData,
    getNextPageParam: (data) => {
      const { pagination = {} } = data;
      const totalPages = Math.ceil(pagination.total / pagination.perPage);
      if (pagination.page < totalPages) {
        return pagination.page + 1;
      }
      return undefined;
    },
  });
};

const markUpdateAsRead = (updates, parentType = "*", parentId = "*") => {
  return updates.reduce((prev, next) => {
    if (next.parent_type === parentType && next.parent_id === parentId) {
      prev.push({
        ...next,
        updates: [],
        notifications_ids: [],
        reactions: [],
      });
    } else if (parentType === "*" && parentId === "*") {
      prev.push({
        ...next,
        updates: [],
        notifications_ids: [],
        reactions: [],
      });
    } else {
      prev.push(next);
    }
    return prev;
  }, []);
};

export const useMarkAsReadMutation = () =>
  useMutation({
    onMutate: (data) => {
      const { parentType, parentId } = data;
      const previous = queryClient.getQueryData(["notifications", "for-screen"]);
      if (previous && previous.pages && previous.pageParams) {
        const next = cloneDeep(previous);

        next.pages.map((page) => {
          page.object_and_updates = markUpdateAsRead(page.object_and_updates, parentType, parentId);
          page.total_unread = parentType === "*" && parentId === "*" ? 0 : page.total_unread - 1;
        });
        queryClient.setQueryData(["notifications", "for-screen"], () => {
          return next;
        });
      }
      return previous;
    },
    mutationFn: (data) => {
      const { parentType, parentId } = data;
      if (!parentType && !parentId) {
        throw new Error("Missing parentType and parentId");
      }
      if (parentType === "*" && parentId === "*") {
        return fetch({
          url: `${Angie.api_url}/notifications/mark-all-as-read`,
          method: `put`,
        });
      }
      return Promise.resolve(true);
    },
    onSuccess: (response) => {
      if (Array.isArray(response) && response.length === 0) {
        queryClient.invalidateQueries(["notifications", "for-screen"]);
      }
    },
  });
